import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import { useState, useEffect } from 'react';
import styled from 'styled-components';

const EditorContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 1rem;
  background: white;
  
  .ProseMirror {
    padding: 1rem;
    min-height: 200px;
    outline: none;
    
    > * + * {
      margin-top: 0.75em;
    }
    
    &:focus {
      border-color: var(--primary-color);
    }
    
    strong {
      font-weight: 600;
    }
    
    ul, ol {
      padding: 0 1rem;
    }
    
    ul {
      list-style-type: disc;
    }
    
    ol {
      list-style-type: decimal;
    }
    
    h1 {
      font-size: 2rem;
      font-weight: 700;
      margin-top: 1rem;
      margin-bottom: 0.5rem;
    }
    
    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      margin-top: 1rem;
      margin-bottom: 0.5rem;
    }
    
    h3 {
      font-size: 1.25rem;
      font-weight: 600;
      margin-top: 1rem;
      margin-bottom: 0.5rem;
    }
    
    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: #adb5bd;
      pointer-events: none;
      height: 0;
    }
    
    img {
      max-width: 100%;
      height: auto;
    }
    
    a {
      color: var(--primary-color);
      text-decoration: underline;
    }
    
    blockquote {
      padding-left: 1rem;
      border-left: 3px solid #ddd;
      color: #666;
      font-style: italic;
    }
    
    pre {
      background: #f8f9fa;
      padding: 0.75rem;
      border-radius: 4px;
      font-family: monospace;
      overflow-x: auto;
    }
    
    code {
      background: #f8f9fa;
      padding: 0.2rem 0.4rem;
      border-radius: 4px;
      font-family: monospace;
      font-size: 0.9em;
    }
  }
`;

const MenuBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0.5rem;
  border-bottom: 1px solid #ddd;
  background: #f8f9fa;
  
  button {
    margin: 0.2rem;
    padding: 0.4rem 0.6rem;
    border: none;
    background: white;
    border-radius: 4px;
    font-size: 0.9rem;
    cursor: pointer;
    color: #333;
    
    &:hover {
      background: #f0f0f0;
    }
    
    &.is-active {
      background: var(--primary-color);
      color: white;
    }
  }
  
  input[type="color"] {
    height: 2rem;
    width: 2rem;
    padding: 0;
    border: none;
    background: none;
    margin: 0.2rem;
  }
  
  select {
    margin: 0.2rem;
    padding: 0.4rem 0.6rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: white;
  }
`;

const ImageInput = styled.div`
  margin: 0.2rem;
  display: flex;
  align-items: center;
  
  input {
    padding: 0.4rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
    margin-right: 0.5rem;
  }
  
  button {
    margin: 0;
  }
`;

function MenuBarComponent({ editor }) {
  const [imageUrl, setImageUrl] = useState('');
  
  if (!editor) {
    return null;
  }
  
  const addImage = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
    }
  };
  
  return (
    <MenuBar>
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
      >
        Bold
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
      >
        Italic
      </button>
      <button
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={editor.isActive('underline') ? 'is-active' : ''}
      >
        Underline
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'is-active' : ''}
      >
        Strike
      </button>
      <button
        onClick={() => editor.chain().focus().setParagraph().run()}
        className={editor.isActive('paragraph') ? 'is-active' : ''}
      >
        Paragraph
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
      >
        H1
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
      >
        H2
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
      >
        H3
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
      >
        Bullet List
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
      >
        Ordered List
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={editor.isActive('blockquote') ? 'is-active' : ''}
      >
        Quote
      </button>
      <button onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        Horizontal Rule
      </button>
      <button onClick={() => editor.chain().focus().setTextAlign('left').run()}>
        Left
      </button>
      <button onClick={() => editor.chain().focus().setTextAlign('center').run()}>
        Center
      </button>
      <button onClick={() => editor.chain().focus().setTextAlign('right').run()}>
        Right
      </button>
      <input 
        type="color" 
        onInput={event => editor.chain().focus().setColor(event.target.value).run()} 
        value={editor.getAttributes('textStyle').color || '#000000'} 
      />
      <ImageInput>
        <input
          type="text"
          placeholder="Image URL"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
        />
        <button onClick={addImage}>
          Add Image
        </button>
      </ImageInput>
    </MenuBar>
  );
}

function TipTapEditor({ content, onUpdate, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      Image,
      Placeholder.configure({
        placeholder: placeholder || 'Write something...',
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Color,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onUpdate(editor.getHTML());
    },
  });

  // Update content when it changes externally
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  return (
    <EditorContainer>
      <MenuBarComponent editor={editor} />
      <EditorContent editor={editor} />
    </EditorContainer>
  );
}

export default TipTapEditor; 